import { notifyApi, type NotificationsView } from './api';

/**
 * Web Push on this device (SPEC_V9 §D3). The service worker (`/sw.js`) only
 * shows notifications and opens the app on tap; it never caches anything.
 * Permission is only ever requested from a user gesture after a soft ask —
 * never on first load.
 */

export const SW_URL = '/sw.js';

export type PushSupport = 'supported' | 'unsupported' | 'denied';

export function pushSupport(): PushSupport {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return 'unsupported';
  return Notification.permission === 'denied' ? 'denied' : 'supported';
}

export function permission(): NotificationPermission | 'unsupported' {
  return typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported';
}

/** Registers the service worker once (no-op where unsupported). */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register(SW_URL, { scope: '/' });
  } catch {
    return null;
  }
}

/** base64url VAPID key → the Uint8Array PushManager.subscribe wants. */
export function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** "Chrome on Android" — a short, non-identifying device label for Settings. */
export function deviceLabel(ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''): string {
  const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const os = /Android/.test(ua) ? 'Android' : /iPhone|iPad|iPod/.test(ua) ? 'iOS' : /Mac OS X/.test(ua) ? 'Mac' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : '';
  return os ? `${browser} on ${os}` : browser;
}

/** This device's current subscription endpoint, if any. */
export async function currentEndpoint(): Promise<string | null> {
  if (pushSupport() === 'unsupported') return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    const sub = await reg?.pushManager.getSubscription();
    return sub?.endpoint ?? null;
  } catch {
    return null;
  }
}

export type EnableResult = { ok: true; view: NotificationsView } | { ok: false; reason: 'unsupported' | 'denied' | 'dismissed' | 'failed' };

/**
 * Asks for permission (must run inside a click handler), subscribes this
 * device and stores it. Safe to call again: it reuses the subscription.
 */
export async function enablePush(vapidKey: string): Promise<EnableResult> {
  if (pushSupport() === 'unsupported') return { ok: false, reason: 'unsupported' };
  let perm = Notification.permission;
  if (perm === 'default') perm = await Notification.requestPermission();
  if (perm === 'denied') return { ok: false, reason: 'denied' };
  if (perm !== 'granted') return { ok: false, reason: 'dismissed' };
  try {
    const reg = (await registerServiceWorker()) ?? (await navigator.serviceWorker.ready);
    let sub = await reg.pushManager.getSubscription();
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(vapidKey) as BufferSource });
    const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
    if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) return { ok: false, reason: 'failed' };
    const view = await notifyApi.subscribePush({ endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth }, device: deviceLabel() });
    return { ok: true, view };
  } catch {
    return { ok: false, reason: 'failed' };
  }
}

/** Unsubscribes this device (browser + server). */
export async function disablePush(): Promise<NotificationsView | null> {
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    const sub = await reg?.pushManager.getSubscription();
    if (!sub) return null;
    const endpoint = sub.endpoint;
    await sub.unsubscribe().catch(() => undefined);
    return await notifyApi.unsubscribePush(endpoint);
  } catch {
    return null;
  }
}
